import * as React from 'react';
import type { AccountInfo } from '@azure/msal-browser';
import type { IWorkspace } from '../../api/src/workspace';
import type { IQueueItem } from '../../api/src/queue';
import { fetchCredits, getQueue, getWorkspace, listWorkspaces, saveWorkspace as save, type IWorkspaceSummary } from '../api';
import { BASE_SCOPES, CLIENT_ID, MANAGE_SCOPES } from '../config';
import { createGraphClient } from '../graph';
import { GraphAuditLog } from '../GraphAuditLog';
import { ReviewList } from '../ReviewList';
import { FirmRecords } from '../records/FirmRecords';
import { AppContext, errorText, type IAppContext, type IToast } from './context';
import { useRoute } from './router';
import { Loading, SignInBanner, StartupError } from './Stuck';
import { AppHeader } from './AppHeader';
import Settings, { AccountOnly } from '../pages/Settings';
import { startupDone } from '../auth';
import { useInbox } from '../filing/useInbox';
import type { IInboxResult } from '../filing/inbox';
import { Shell } from './Shell';
import SignIn from '../pages/SignIn';
import { OnboardingInApp, OnboardingStart } from '../pages/Onboarding';
import { SetupPreview } from './SetupPreview';

const LAST_LIBRARY: string = 'aiadmin.driveId';

function remembered(): string | undefined {
  try {
    return localStorage.getItem(LAST_LIBRARY) ?? undefined;
  } catch {
    return undefined;
  }
}

function remember(driveId: string): void {
  try {
    localStorage.setItem(LAST_LIBRARY, driveId);
  } catch {
    // Chosen again next visit; nothing else depends on it.
  }
}

export default function App(props: { account: AccountInfo | undefined }): React.ReactElement {
  if (!CLIENT_ID) {
    return <main className="center-page"><p>The app isn't configured yet: set VITE_CLIENT_ID in web/.env.</p></main>;
  }
  if (!props.account) {
    return <SignIn />;
  }
  return <SignedIn account={props.account} />;
}

function SignedIn(props: { account: AccountInfo }): React.ReactElement {
  const [route, go] = useRoute();
  const graph = React.useMemo(() => createGraphClient(BASE_SCOPES), []);
  const manageGraph = React.useMemo(() => createGraphClient(MANAGE_SCOPES), []);
  // The Filing Review list is kept up to date on the side: never a sign-in or consent prompt for it.
  const quietManageGraph = React.useMemo(() => createGraphClient(MANAGE_SCOPES, false), []);
  const [workspaces, setWorkspaces] = React.useState<IWorkspaceSummary[] | undefined>(undefined);
  const [workspace, setWorkspace] = React.useState<IWorkspace | undefined>(undefined);
  const [queue, setQueue] = React.useState<IQueueItem[]>([]);
  const [credits, setCredits] = React.useState<number | undefined>(undefined);
  const [plan, setPlan] = React.useState<'trial' | 'paid'>('trial');
  const [trial, setTrial] = React.useState<import('../api').ITrialAllowance | undefined>(undefined);
  const refreshPlan = React.useCallback(() => {
    fetchCredits().then(c => { setCredits(c.balance); setPlan(c.plan === 'paid' ? 'paid' : 'trial'); setTrial(c.trial); }).catch(() => undefined);
  }, []);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [toast, setToast] = React.useState<IToast | undefined>(undefined);

  const load = React.useCallback(async (driveId?: string): Promise<void> => {
    setError(undefined);
    try {
      const all: IWorkspaceSummary[] = await listWorkspaces();
      setWorkspaces(all);
      const pick: string | undefined = driveId ?? remembered();
      const chosen: IWorkspaceSummary | undefined = all.filter(w => w.driveId === pick)[0] ?? all[0];
      if (chosen) {
        remember(chosen.driveId);
        setWorkspace(await getWorkspace(chosen.driveId));
        setQueue(await getQueue(chosen.driveId));
      } else {
        setWorkspace(undefined);
        setQueue([]);
      }
    } catch (e) {
      setError(errorText(e));
    } finally {
      // From now on, background work that needs Microsoft shows a banner instead of leaving the page.
      startupDone();
    }
  }, []);

  React.useEffect(() => { void load(); }, [load]);
  React.useEffect(refreshPlan, [refreshPlan]);
  React.useEffect(() => {
    if (!toast) {
      return undefined;
    }
    const t = setTimeout(() => setToast(undefined), 8000);
    return () => clearTimeout(t);
  }, [toast]);

  const audit = React.useMemo(
    () => (workspace ? new GraphAuditLog(graph, manageGraph, workspace.siteId, siteUrlOf(workspace.webUrl)) : undefined),
    [graph, manageGraph, workspace?.siteId, workspace?.webUrl]
  );

  const records = React.useMemo(
    () => (workspace && audit ? new FirmRecords(graph, manageGraph, audit, workspace, props.account.username, siteUrlOf(workspace.webUrl)) : undefined),
    [graph, manageGraph, audit, workspace?.siteId, workspace?.driveId, workspace?.firm?.restricted?.driveId, props.account.username]
  );

  const reviewList = React.useMemo(
    () => (workspace ? new ReviewList(quietManageGraph, workspace.siteId, window.location.origin) : undefined),
    [quietManageGraph, workspace?.siteId]
  );

  const inbox = useInbox({
    graph, workspace, audit, reviewList, user: props.account.username,
    onDone: (r: IInboxResult) => {
      if (workspace) {
        getQueue(workspace.driveId).then(setQueue).catch(() => undefined);
      }
      refreshPlan();
      if (r.filed > 0 || r.queued > 0) {
        setToast({ text: inboxSummary(r) });
      }
    }
  });

  if (error) {
    return <StartupError message={error} onRetry={() => { setWorkspaces(undefined); void load(); }} />;
  }
  if (!workspaces) {
    return <Loading label="Loading your libraries…" onRetry={() => window.location.reload()} />;
  }
  // Every signed-in screen sits under the same header (branding left, profile right).
  const framed = (node: React.ReactNode, search?: boolean): React.ReactElement => (
    <div className="app-frame">
      <AppHeader account={props.account} trial={plan !== 'paid'} search={search} />
      <div className="app-body">{node}</div>
    </div>
  );

  if (!workspace && route === 'settings') {
    return framed(<AccountOnly account={props.account} graph={graph} libraries={workspaces} />);
  }
  // Before setup the app shows itself locked, with one way in: "Set up your account".
  if (!workspace && route !== 'setup' && route !== 'connect') {
    return framed(<SetupPreview onStart={() => go('setup')} />);
  }
  if (!workspace || route === 'connect') {
    return framed(
      <OnboardingStart
        graph={graph}
        account={props.account}
        connected={workspaces}
        onConnected={async w => { await load(w.driveId); go(w.setupDone ? 'home' : 'setup'); }}
        onCancel={() => go('home')}
      />
    );
  }

  const saveWorkspace = async (next: IWorkspace): Promise<IWorkspace> => {
    const saved: IWorkspace = await save(next);
    setWorkspace(saved);
    return saved;
  };

  const ctx: IAppContext = {
    account: props.account, graph, manageGraph, workspaces, workspace, saveWorkspace,
    switchLibrary: driveId => { void load(driveId); go('home'); },
    audit: audit!, records: records!, queue,
    reloadQueue: async () => {
      const items: IQueueItem[] = await getQueue(workspace.driveId);
      setQueue(items);
      // Someone handled documents on Review: the Filing Review list follows. Best effort, never blocks.
      reviewList?.sync(items).catch(e => console.warn('Could not update the Filing Review list', errorText(e)));
    },
    credits, setCredits, plan, trial, refreshPlan, route, go,
    toast: setToast,
    inbox
  };

  return (
    <AppContext.Provider value={ctx}>
      <SignInBanner />
      {route === 'settings' && !workspace.setupDone
        ? framed(<Settings />)
        : route === 'setup'
          ? framed(<OnboardingInApp key={workspace.driveId} />)
          : !workspace.setupDone
            ? framed(<SetupPreview started onStart={() => go('setup')} />)
            : framed(<Shell />, true)}
      {toast && (
        <div className="toast-bar" role="status">
          <span>{toast.text}</span>
          {toast.action && <button type="button" onClick={() => { toast.action!.run(); setToast(undefined); }}>{toast.action.label}</button>}
        </div>
      )}
    </AppContext.Provider>
  );
}

export function inboxSummary(r: IInboxResult): string {
  const parts: string[] = [];
  if (r.filed) {
    parts.push(`Filed ${r.filed} document${r.filed === 1 ? '' : 's'}`);
  }
  if (r.queued) {
    parts.push(`${r.queued} need${r.queued === 1 ? 's' : ''} you in the review queue`);
  }
  return parts.length ? `${parts.join(', ')}.` : 'Nothing new in Inbox.';
}

/** The site's address from a library address (…/sites/Clients/Shared Documents → …/sites/Clients). */
export function siteUrlOf(libraryUrl: string): string {
  const m: RegExpMatchArray | null = libraryUrl.match(/^(https:\/\/[^/]+(?:\/(?:sites|teams)\/[^/]+)?)/i);
  return m ? m[1] : libraryUrl;
}
